import React, { useEffect, useRef, useState } from "react";

import AppPlaceholderPhoto from "@common/assets/images/shared-icons/app-icon-placeholder.svg";
import ChromeAppPlaceholderPhoto from "@common/assets/images/shared-icons/purple-app-icon-placeholder.svg";
import { AddAppButton } from "@common/components/AddAppButton/AddAppButton";
import Header from "@common/components/Header/Header";
import Icon from "@common/components/Icon/Icon";
import { SubNavigation } from "@common/components/Navigation/SubNavigation/SubNavigation.styles";
import PageContent from "@common/components/PageContent/PageContent";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { ScrollContainer } from "@common/components/ScrollContainer/ScrollContainer";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { App, Workspace } from "@common/models";
import { isAdmin } from "@common/models/Member.model";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { getOSImage } from "@common/util/contentResolvers";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useShakingAnimation } from "@main/context/ShakingAnimation/ShakingAnimationContext";
import { FreeAppBadge } from "@main/layouts/views/AdministrationLayout.styles";
import { RoutePaths } from "@main/router/config/routePaths";
import { administrationRouteConfigs, PathRouteConfig, RouteConfigType } from "@main/router/config/routesConfig";
import { generatePath, matchPath, NavLink, Outlet, useLocation, useParams } from "react-router-dom";

import * as Styled from "./AppsLayout.styles";
import RoleProtected from "../components/RoleProtected";

export default function AdministrationLayoutView() {
  const { selectedWorkspace } = useAppSelectionContext();
  const location = useLocation();

  const [collapseApps, setCollapseApps] = useState(!location.pathname.includes("/apps/"));

  const { apps } = useAppContext();
  const { appKey } = useParams();

  useEffect(() => {
    if (location.pathname.includes("/apps/")) {
      setCollapseApps(false);
    }
  }, [location]);

  if (!selectedWorkspace) {
    return <Spinner />;
  }

  const administrationLinks = administrationRouteConfigs
    .filter((routeConfig) => routeConfig.type === RouteConfigType.PATH)
    .map((pathRouteConfig) => {
      const config = pathRouteConfig as PathRouteConfig;
      return mapRouteConfigToNavLink(
        config,
        selectedWorkspace,
        !!matchPath(config.path, location.pathname),
        isAdmin(selectedWorkspace?.role),
        apps,
        collapseApps,
        setCollapseApps,
        appKey,
      );
    })
    .filter(Boolean);

  return (
    <>
      <Header>{selectedWorkspace?.name} workspace administration</Header>
      <Flex style={{ flex: 1, paddingLeft: "2rem", height: "100%" }}>
        <ScrollContainer>
          <Flex>
            <div style={{ marginBottom: "4rem" }}>
              <SubNavigation>
                <ul>{administrationLinks}</ul>
              </SubNavigation>
            </div>
            <RoleProtected>
              <div style={{ width: "100%" }}>
                <PageContent>
                  <Outlet />
                </PageContent>
              </div>
            </RoleProtected>
          </Flex>
        </ScrollContainer>
      </Flex>
    </>
  );
}

const RegisteredAddAppButton = ({ id, workspaceSlug }: { id: string; workspaceSlug: string }) => {
  const { registerShakeable } = useShakingAnimation();
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerShakeable(id, buttonRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <AddAppButton
      navigateTo={generatePath(RoutePaths.ADD_APP, { workspaceSlug: workspaceSlug })}
      wiggle={false}
      id={id}
      ref={buttonRef}
    />
  );
};

const mapRouteConfigToNavLink = (
  config: PathRouteConfig,
  workspace: Workspace,
  isActive: boolean,
  isUserAdmin: boolean,
  apps: App[],
  collapseApps: boolean,
  setCollapseApps: (collapse: boolean) => void,
  activeAppKey?: string,
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const to = generatePath(config.path, { workspaceSlug: workspace.slug });

  if (config.protected && !isUserAdmin) return;

  const isAppsNavLink = config.path.includes("apps");

  const hasApps = apps?.length > 0;
  const hasActiveApps = Boolean(apps.find((a) => a.active));
  const hasArchivedApps = Boolean(apps.find((a) => !a.active));

  return (
    <React.Fragment key={config.path ?? config.name}>
      <li className={isActive ? "active" : ""}>
        {!isAppsNavLink ? (
          <NavLink
            to={to}
            data-testid={config.testId}
          >
            {config.name}
            <Icon
              icon={"caret-right"}
              size={10}
            />
          </NavLink>
        ) : (
          <Styled.BaseParagraph
            onClick={() => setCollapseApps(!collapseApps)}
            $active={!collapseApps}
          >
            Apps
            <Icon
              icon={!collapseApps ? "caret-down" : "caret-right"}
              size={10}
            />
          </Styled.BaseParagraph>
        )}
      </li>

      {isAppsNavLink && !collapseApps && hasApps && (
        <Styled.Container>
          {hasActiveApps && (
            <>
              <Styled.Subtitle
                fontSize={12}
                color={vars.colors.grey70}
                $paddingTop
              >
                Active
              </Styled.Subtitle>
              <RegisteredAddAppButton
                id={"AddAppButton"}
                workspaceSlug={workspace.slug}
              />
              {getAppList(
                apps.filter((a) => a.active),
                workspace.slug,
                activeAppKey,
              )}
            </>
          )}

          {hasArchivedApps && (
            <>
              <Styled.Subtitle
                fontSize={12}
                color={vars.colors.grey70}
                $paddingTop
              >
                Inactive
              </Styled.Subtitle>
              {getAppList(
                apps.filter((a) => !a.active),
                workspace.slug,
                activeAppKey,
              )}
            </>
          )}
        </Styled.Container>
      )}

      {isAppsNavLink && !collapseApps && !hasApps && (
        <Styled.Container>
          <Styled.Subtitle
            fontSize={12}
            color={vars.colors.grey70}
            $paddingTop
          >
            No apps
          </Styled.Subtitle>
        </Styled.Container>
      )}
    </React.Fragment>
  );
};

const getAppList = (apps: App[], workspaceSlug: string, activeAppKey?: string) => {
  return apps.map((app) => {
    return (
      <li
        key={app.key}
        className={app.key === activeAppKey ? "active" : ""}
      >
        <NavLink
          style={{ fontSize: "1.4rem", paddingTop: "0.9rem", paddingBottom: "1.1rem" }}
          to={generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL_KEY, {
            workspaceSlug: workspaceSlug,
            appKey: app.key,
          })}
        >
          <Flex $alignItems="center">
            <img
              src={app.logo_url ? app.logo_url : app.is_extension ? ChromeAppPlaceholderPhoto : AppPlaceholderPhoto}
              width={28}
              height={28}
              style={{ borderRadius: "0.8rem", objectFit: "cover", marginRight: "0.8rem" }}
            />
            <Paragraph fontSize={14}>{app.name}</Paragraph>
            <img
              src={getOSImage(app.platform)}
              style={{ marginLeft: "0.4rem" }}
              width={20}
              height={20}
            />
            {app.is_extension || app.is_sample ? (
              <Tooltip
                style={{ width: "auto" }}
                text="Chrome extension apps are free"
                position="top"
              >
                <FreeAppBadge>{"Free"}</FreeAppBadge>
              </Tooltip>
            ) : null}
          </Flex>
        </NavLink>
      </li>
    );
  });
};
